<template>
  <modal
    v-if="active"
    bodyOverflow="auto"
    :height="userPermissions.accessToExistingItems === 'onlySelected' && '1000px'"
    bodyPadding="0"
    :title="t('Permissions for {0}', [activeWorkspace.name])"
    @close="close"
    :read-article-url="`https://docs.bemob.com/${locale}/workspaces`"
  >
    <div :class="dialogContentClass">
      <div class="content contentWrp">
        <div class="top">
          <div class="email mb-8">
            <ui-icon
              size="16"
              :color="$colors.green"
              class="mr-4"
              icon="fas fa-envelope"
            ></ui-icon>
            <div class="email-text">{{ user.login }}</div>
          </div>
          <ui-checkbox
            :label="t('Hide profit')"
            :help-tooltip="
              t(
                'All the values connected with Conversions, Revenue, Profit will be displayed as 0 in statistics.',
              )
            "
            class="ml-4"
            v-model="userPermissions.hideProfit"
          />
          <ui-checkbox
            v-if="userPermissions.hideProfit"
            class="ml-4"
            v-model="userPermissions.showConversions"
            :label="t('Show Conversions')"
          />
          <template v-if="user.role === 'user'">
            <label class="font-weight-bold mt-8 d-block text-capitalize">
              {{ t('User allowed to create') }}
            </label>
            <div class="d-flex">
              <div class="flex-grow-1">
                <ui-checkbox
                  class="ml-4"
                  v-model="userPermissions.canCreate.campaigns"
                  :label="t('Campaigns')"
                />
                <ui-checkbox
                  class="ml-4"
                  v-model="userPermissions.canCreate.trafficSources"
                  :label="t('Traffic sources')"
                />
              </div>
              <div class="flex-grow-1">
                <ui-checkbox
                  class="ml-4"
                  v-model="userPermissions.canCreate.flows"
                  :label="t('Flows')"
                />
                <ui-checkbox
                  class="ml-4"
                  v-model="userPermissions.canCreate.landings"
                  :label="t('Landings')"
                />
              </div>
              <div class="flex-grow-1">
                <ui-checkbox
                  class="ml-4"
                  v-model="userPermissions.canCreate.offers"
                  :label="t('Offers')"
                />
                <ui-checkbox
                  class="ml-4"
                  v-model="userPermissions.canCreate.affiliateNetworks"
                  :label="t('All affiliate networks')"
                />
              </div>
            </div>
            <label class="font-weight-bold mt-8 d-block text-capitalize">
              {{ t('Access to existing items') }}
            </label>
            <ui-radio-group v-model="userPermissions.accessToExistingItems">
              <div class="d-flex mt-8 mb-8">
                <ui-radio value="fullAccess" :label="t('Full access')" />
                <ui-radio
                  class="ml-16"
                  value="onlySelected"
                  :label="t('Only selected items')"
                />
              </div>
            </ui-radio-group>
            <ui-info
              class="mb-16"
              :html="
                t(
                  'Users receive the Full access to all the elements by default.<br> It is possible to change the access type in Permissions settings below.',
                )
              "
            />
          </template>
        </div>
        <div v-if="userPermissions.accessToExistingItems === 'onlySelected'" class="tabs">
          <div class="tabs-head mt-16">
            <div
              v-for="key in tabs"
              :key="key"
              @click="tab = key"
              :class="{
                'tab-head-item': true,
                'tab-head-item--active': tab === key,
              }"
            >
              <ui-icon class="mr-4" :icon="`fas ${tabsData[key].icon}`" />
              {{ tabsData[key].title }}
            </div>
          </div>
          <div class="tab-body">
            <div
              v-for="key in tabs"
              :key="key"
              :class="{
                'tab-body-item': true,
                'tab-body-item--active': tab === key,
              }"
            >
              <div class="tab-body-title-block">
                <div class="tab-body-title">
                  <ui-icon size="16" class="mr-4" :icon="`fas ${tabsData[key].icon}`" />
                  <span class="tab-body-title-text">{{ header }}</span>
                  <div class="mt-0" v-if="genDependConstraint('*')">
                    <v-tooltip :color="$colors.pink" min-width="300" :attach="true">
                      <template #activator="{ props }">
                        <ui-icon
                          v-bind="props"
                          class="warning-icon warning-icon-title ml-4 mb-2"
                          :size="12"
                          :color="$colors.tangerine"
                          icon="fas fa-exclamation-triangle"
                        />
                      </template>
                      <div>
                        <div>{{ t('Access type should not be lower than') }}</div>
                        <ul class="gen-depend-constraint-list">
                          <li v-for="row in genDependConstraint('*')">{{ row }}</li>
                        </ul>
                      </div>
                    </v-tooltip>
                  </div>
                </div>

                <ui-select
                  class="access-select"
                  :items="accessAll"
                  :modelValue="currentAcl['*'] || 'inherit'"
                  item-title="text"
                  item-value="value"
                  :mini="true"
                  key="*"
                  :autocomplete="false"
                  :ref="`select_${tab}_*`"
                  @update:model-value="event => onSelectChange(event, '*')"
                />
              </div>

              <ui-input
                v-model="search"
                :placeholder="t('Search')"
                prepend-inner-icon="fas fa-search"
              />
              <div class="no-data-in-table" v-if="filteredRowsByTab[key].length === 0">
                <img width="70" src="/images/empty-table.png" />
                <div class="no-data-in-table__title">
                  {{ t('No Data Found') }}
                </div>
              </div>
              <RecycleScroller
                :items="filteredRowsByTab[key]"
                class="scroller"
                key-field="id"
                :item-size="34"
                v-slot="{ item }"
              >
                <div class="table-row">
                  <div class="table-cell">
                    {{ item.nameFull || item.name }}
                    <div class="mt-0" v-if="genDependConstraint(item.id)">
                      <v-tooltip
                        :color="$colors.pink"
                        min-width="300"
                        :attach="true"
                        :close-on-content-click="true"
                      >
                        <template #activator="{ props }">
                          <ui-icon
                            v-bind="props"
                            class="warning-icon warning-icon-title ml-4 mb-2"
                            :size="12"
                            :color="$colors.tangerine"
                            icon="fas fa-exclamation-triangle"
                          />
                        </template>
                        <div>
                          <div>{{ t('Access type should not be lower than') }}</div>
                          <ul class="gen-depend-constraint-list">
                            <li v-for="row in genDependConstraint(item.id)">{{ row }}</li>
                          </ul>
                        </div>
                      </v-tooltip>
                    </div>
                  </div>
                  <div class="table-cell">
                    <template v-if="showSelectForId === item.id">
                      <ui-select
                        class="access-select"
                        :items="access"
                        :modelValue="currentAcl[item.id] || 'inherit'"
                        item-title="text"
                        item-value="value"
                        :mini="true"
                        key="*"
                        hide-details="1"
                        :autocomplete="false"
                        :ref="`select_${tab}_*`"
                        @update:model-value="event => onSelectChange(event, item)"
                      />
                      <button class="ml-8 table-cross-button" @click="penClick(null)">
                        <ui-icon
                          :class="$colors.graphite"
                          size="12"
                          icon="fas fa-times"
                        />
                      </button>
                    </template>
                    <template v-if="showSelectForId !== item.id">
                      <button class="ml-8 table-pen-button" @click="penClick(item)">
                        <span class="mr-8 readModeText">{{
                          accessObj[currentAcl[item.id] || 'inherit'].text
                        }}</span>
                        <ui-icon :class="$colors.graphite" size="12" icon="fas fa-pen" />
                      </button>
                    </template>
                  </div>
                </div>
              </RecycleScroller>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <section class="justify-end xs12" style="display: flex">
        <ui-button :text="t('Cancel')" icon="fas fa-times" @click="close"></ui-button>

        <ui-button
          :text="t('Save')"
          icon="fas fa-check"
          button-style="accent"
          @click="save"
        ></ui-button>
      </section>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import {
  getMemberAclApiRequest,
  getMemberItemsRelationsApiRequest,
  putMemberAclApiRequest,
} from '@/services/api/user-api';
import { useColors, useI18n } from '@/composables';
import { useStore } from '@/store';
import $emitter from '@/services/emitter';
import { useEditAccessPermissions } from './useEditAccessPermissions';
import type {
  PermissionsDialogMember,
  PermissionsDialogWorkspace,
} from './useEditAccessPermissionsConstants';

const props = defineProps<{
  data?: {
    user?: PermissionsDialogMember;
    activeWorkspace?: PermissionsDialogWorkspace;
  };
}>();

const emit = defineEmits<{ close: [] }>();

const { t, locale } = useI18n();
const $colors = useColors();
const store = useStore();

const {
  tabs,
  showSelectForId,
  active,
  userPermissions,
  rows,
  search,
  itemsRelationsObject,
  tab,
  user,
  activeWorkspace,
  filteredRowsByTab,
  accessObj,
  access,
  accessAll,
  tabsData,
  dialogContentClass,
  header,
  currentAcl,
  genDependConstraint,
  penClick,
  onSelectChange,
  buildItemsRelations,
  resetPermissionsState,
} = useEditAccessPermissions();

const close = () => {
  emit('close');
};

const save = () => {
  void putMemberAclApiRequest(user.value.id, {
    ...userPermissions.value,
    workspaceId: activeWorkspace.value.id,
  }).then(() => {
    close();
    $emitter.emit('toast-success', t('Permissions successfully changed.'));
  });
};

onMounted(async () => {
  store.setLoading(true);
  activeWorkspace.value = props.data?.activeWorkspace ?? { id: '', name: '' };
  resetPermissionsState();

  if (props.data?.user?.id) {
    user.value = props.data.user;
    const response = await getMemberAclApiRequest({
      userId: props.data.user.id,
      workspaceIds: [activeWorkspace.value.id],
    });
    userPermissions.value = {
      ...userPermissions.value,
      ...response[0],
    };
  }

  if (user.value.role === 'admin') {
    active.value = true;
    store.setLoading(false);
    return;
  }

  const itemsRows = (await getMemberItemsRelationsApiRequest({
    workspaceId: activeWorkspace.value.id,
  })) as Record<string, unknown[]>;

  rows.value = itemsRows;
  active.value = true;

  try {
    itemsRelationsObject.value = buildItemsRelations(itemsRows);
  } catch (error) {
    console.error('error', error);
  } finally {
    store.setLoading(false);
  }
});
</script>

<style lang="scss">
.access-permissions-dialog.v-dialog.v-dialog--active > .v-card > .v-card__text {
  padding: 0 !important;
}

.access-permissions-dialog {
  width: 100%;
  max-height: 900px !important;
  .v-card {
    height: 100%;
  }
  &--onlySelected {
    height: 100%;
    max-width: 1200px !important;
  }
  @media all and (max-width: 1030px) {
    width: 717px !important;
  }
}
</style>
<style lang="scss" scoped>
@import '@/styles/dialogs/permissions-dialog';
.gen-depend-constraint-list {
  list-style-position: inside;
}
.contentWrp {
  height: 100%;
}

.top {
  padding: 16px;
  width: 700px;
  max-width: initial;
  flex-grow: 0;
  @media all and (max-width: 1030px) {
    width: 680px;
  }
}

.email {
  font-size: 16px;
  font-weight: bold;
  display: flex;
  align-items: center;
}

.email-text {
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.access-select) {
  width: 130px;

  .v-icon {
    font-size: 13px !important;
  }
}

:deep(.table) {
  .v-table__overflow {
    overflow: auto;
    height: 100%;
  }
  td:nth-child(1) {
    width: 507px !important;
    padding-right: 16px !important;
    @media all and (max-width: 1030px) {
      width: 490px !important;
    }
  }
  td:nth-child(2) {
    width: 100px;
  }
}

.tab-body {
  height: calc(100% - 66px);
  box-shadow: 0 2px 4px 0 var(--color-black10) !important;
  border-top: 1px solid var(--color-gray3);
}

.tabs {
  margin-right: 16px;
  @media all and (max-width: 1030px) {
    margin-right: 0;
    height: 600px;
    width: 700px;
  }
}

.select-with-button {
  display: flex;
}

.table-pen-button {
  display: flex;
  align-items: center;
  .ui-icon {
    visibility: hidden;
  }
}

.table-pen-button:hover {
  color: var(--color-green) !important;
}

:deep(.table-pen-button:hover .ui-icon) {
  color: var(--color-green) !important;
}

.table-row:hover .table-pen-button .ui-icon {
  visibility: visible;
}

:deep(.table-cross-button:hover .ui-icon) {
  color: var(--color-green) !important;
}

.readModeText {
  height: 24px;
  white-space: nowrap;
  align-items: center;
  display: flex;
}

.table-row {
  z-index: auto;
}

.table-cell:first-child {
  flex-grow: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-cell:last-child {
  width: 160px;
  flex-shrink: 0;
}

ui-select > div {
  width: 130px;
}
.scroller {
  height: 100%;
}
</style>
